import { Box, Grid, Typography } from "@mui/material"
import { MAX_EMAIL_LENGTH } from "../../../../../../constants/inputValidationDefaults"
import { makeStyles } from "tss-react/mui"

import { StandardAlertInfo } from "../../../../../../shared/components/alert/StandardAlertInfo"
import { FormikSwitch, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { StandardMainPanel } from "../../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../../shared/components/forms/StandardSidePanel"
import { StandardForm } from "../../../../../../shared/components/forms/StandardForm"
import { MatchEnvironment } from "../../../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../../../environment/envTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"

import { HistoryType, WorkgroupFormValues } from "../workgroupsTypes"
import { WorkgroupMembersPanel } from "./WorkgroupMembersPanel"

// Members editing waits for backend support (GET workgroup does not return members yet), so it stays off prod.
const MEMBERS_PANEL_ENVIRONMENTS = [EnvironmentType.Local, EnvironmentType.Test]

const useStyles = makeStyles()((theme) => ({
    halfWidth: {
        width: "50%",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
    infoAlert: {
        width: "100%",
    },
}))

type CreateUpdateWorkgroupFormContentProps = Readonly<{
    history?: HistoryType
}>

export function CreateUpdateWorkgroupFormContent({ history }: CreateUpdateWorkgroupFormContentProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    return (
        <StandardForm>
            <StandardMainPanel>
                <Box className={classes.halfWidth}>
                    <LocalizedStrict
                        id="create-or-edit-company-workgroup-field-name"
                        attrs={{ label: true, placeholder: true }}
                    >
                        <FormikTextField name="name" label="Name" placeholder="Enter name" required fullWidth />
                    </LocalizedStrict>
                </Box>
                <Box className={classes.halfWidth}>
                    <LocalizedStrict
                        id="create-or-edit-company-workgroup-field-calendar-email"
                        attrs={{ label: true, placeholder: true }}
                    >
                        <FormikTextField
                            name="calendarEmailAddress"
                            maxLength={MAX_EMAIL_LENGTH}
                            label="Calendar email address"
                            placeholder="Enter calendar email address"
                            required
                            fullWidth
                        />
                    </LocalizedStrict>
                </Box>
                <LocalizedStrict
                    id="create-or-edit-company-workgroup-field-calendar-email-info"
                    attrs={{ title: true }}
                >
                    <StandardAlertInfo title="" className={classes.infoAlert} />
                </LocalizedStrict>
                <LocalizedStrict
                    id="create-or-edit-company-workgroup-field-description"
                    attrs={{ label: true, placeholder: true }}
                >
                    <FormikTextField
                        name="description"
                        label="Description for users"
                        placeholder="Enter description for users"
                        multiline
                        minRows={3}
                        maxRows={5}
                        fullWidth
                    />
                </LocalizedStrict>
                <LocalizedStrict id="create-or-edit-company-workgroup-field-description-info" attrs={{ title: true }}>
                    <StandardAlertInfo title="" className={classes.infoAlert} />
                </LocalizedStrict>
            </StandardMainPanel>
            <LocalizedStrict id="create-or-edit-company-workgroup-settings" attrs={{ title: true }}>
                <StandardSidePanel title="Settings" variant="collapsible">
                    <LocalizedStrict id="create-or-edit-company-workgroup-field-active">
                        <FormikSwitch<WorkgroupFormValues> name="isActive">Active</FormikSwitch>
                    </LocalizedStrict>
                </StandardSidePanel>
            </LocalizedStrict>
            <MatchEnvironment environments={MEMBERS_PANEL_ENVIRONMENTS}>
                <LocalizedStrict id="create-or-edit-company-workgroup-members" attrs={{ title: true }}>
                    <StandardSidePanel title="Members" variant="collapsible">
                        <WorkgroupMembersPanel />
                    </StandardSidePanel>
                </LocalizedStrict>
            </MatchEnvironment>
            {history && (
                <LocalizedStrict id="create-or-edit-company-workgroup-history" attrs={{ title: true }}>
                    <StandardSidePanel title="History" variant="collapsible">
                        <Grid container item>
                            <Grid item xs={12} sm={5} justifyContent="space-between">
                                <LocalizedStrict id="history-created">
                                    <Typography variant="body2">Created:</Typography>
                                </LocalizedStrict>
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <Typography variant="body2" textAlign="right">
                                    {dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(history.created.timestamp))}
                                </Typography>
                                <Typography variant="body2" textAlign="right">
                                    {history.created.author ?? "-"}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid container item>
                            <Grid item xs={12} sm={5}>
                                <LocalizedStrict id="history-edited">
                                    <Typography variant="body2">Edited:</Typography>
                                </LocalizedStrict>
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <Typography variant="body2" textAlign="right">
                                    {history.updated
                                        ? dateTimeFactory.formatDateTime(
                                              dateTimeFactory.fromApi(history.updated.timestamp),
                                          )
                                        : "-"}
                                </Typography>
                                <Typography variant="body2" textAlign="right">
                                    {history.updated?.author ?? "-"}
                                </Typography>
                            </Grid>
                        </Grid>
                    </StandardSidePanel>
                </LocalizedStrict>
            )}
        </StandardForm>
    )
}
